import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { DealForm } from '../components/DealForm';
import { useCreateDeal, useDeal, useUpdateDeal } from '../queries';
import type { DealInput } from '../types';

export function DealFormPage() {
  const { publicId = '' } = useParams();
  const isEdit = Boolean(publicId);
  const dealId = publicId;
  const navigate = useNavigate();

  const dealQuery = useDeal(isEdit ? dealId : '');
  const create = useCreateDeal();
  const update = useUpdateDeal(dealId);

  async function handleSubmit(values: DealInput) {
    const saved = isEdit ? await update.mutateAsync(values) : await create.mutateAsync(values);
    navigate(`/crm/deals/${saved.public_id}`);
  }

  if (isEdit && dealQuery.isPending) {
    return (
      <div className="max-w-xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-80 w-full" />
      </div>
    );
  }

  const deal = dealQuery.data;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(isEdit ? `/crm/deals/${dealId}` : '/crm/deals')}
        >
          <ArrowLeft />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">
          {isEdit ? `Edit ${deal?.name ?? 'deal'}` : 'New deal'}
        </h1>
      </div>

      <DealForm
        submitLabel={isEdit ? 'Save changes' : 'Create deal'}
        defaultValues={
          deal
            ? {
                name: deal.name,
                company: String(deal.company),
                primary_contact: deal.primary_contact ? String(deal.primary_contact) : '',
                lead: deal.lead ? String(deal.lead) : '',
                owner: deal.owner ? String(deal.owner) : '',
                stage: deal.stage,
                amount: deal.amount ?? '',
                currency: deal.currency ?? 'USD',
                probability: String(Number(deal.probability)),
                commission_pct: deal.commission_pct ?? '',
                commission_basis: deal.commission_basis,
                expected_close_date: deal.expected_close_date ?? '',
                lost_reason: deal.lost_reason,
                lost_note: deal.lost_note,
              }
            : undefined
        }
        onSubmit={handleSubmit}
        onCancel={() => navigate(isEdit ? `/crm/deals/${dealId}` : '/crm/deals')}
      />
    </div>
  );
}
